import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import BaseAutocomplete from '../form/BaseAutocomplete.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import { InputSwitch } from 'primereact/inputswitch';
import { useLazyGetPrivateUsersQuery } from '../../api/misc.js';

const PrivateUsersListItem = ({
    text,
    isMarked,
    errors,
    className,
    onChangeSwitch,
    onChangeMark,
    disabled,
    modelSharedUsers,
    permission,
    readOnly
}) => {
    const [showSearch, setShowSearch] = useState(true);
    const [search, setSearch] = useState('');
    const [privateUsersList, setPrivateUsersList] = useState([]);
    const [selectedList, setSelectedList] = useState([]);
    const [firstRender, setFirstRender] = useState(true);

    const debouncedSearch = useDebounce(search, 500);

    const [getPrivateUsers, { isLoading: isLoadingPrivateUsers }] = useLazyGetPrivateUsersQuery();

    const selectUser = (id) => {
        handleMark(id, true);
        setSearch('');
    };

    const resetSearch = () => {
        setShowSearch(true);
        setPrivateUsersList([]);
        setSearch('');
    };

    const handleOnChange = (e) => {
        onChangeSwitch(e.value);
        setSelectedList([]);
    };

    const handleMark = (id, value) => {
        const user = privateUsersList.find((item) => item.id === id);
        onChangeMark(id, value);
        if (value) {
            setSelectedList([...selectedList, user]);
        } else {
            setSelectedList(selectedList.filter((u) => u.id !== id));
        }
    };

    const renderPrivate = () => {
        return (
            <>
                {showSearch && (
                    <div className='flex justify-between items-center'>
                        <div className='flex-grow'>
                            <BaseAutocomplete
                                items={privateUsersList.map((user) => ({
                                    label: user.name,
                                    value: user.id
                                }))}
                                onSelect={selectUser}
                                name='search'
                                value={search}
                                placeholder='Search...'
                                setValue={(value) => setSearch(value)}
                                isLoading={isLoadingPrivateUsers}
                                exclude={selectedList.map((item) => item.id)}
                            />
                        </div>
                        <div className='text-theme1-primary2 cursor-pointer ml-4' onClick={() => resetSearch()}>
                            Reset
                        </div>
                    </div>
                )}
                {selectedList && selectedList.length > 0 && (
                    <div>
                        <p className={'text-theme1-text2 mt-4 mb-1'}>Shared with:</p>
                        <div className='flex items-center flex-wrap'>
                            {selectedList.map(({ id, name }, index) => {
                                return (
                                    <div
                                        key={id}
                                        className='min-w-46 flex items-center justify-center space-x-4 flex-row rounded-4xl border border-theme1-text3 px-4 py-2 mr-2 mb-2'
                                    >
                                        <div className={`text-theme1-primary2`}>{name}</div>
                                        <div
                                            className='font-bold text-theme1-primary2'
                                            onClick={() => handleMark(id, false)}
                                        >
                                            x
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </>
        );
    };

    useEffect(() => {
        (async () => {
            if (debouncedSearch !== '') {
                const result = await getPrivateUsers({
                    permission: permission,
                    params: { search: debouncedSearch }
                });
                if (result.data?.response?.users) {
                    setPrivateUsersList(result.data.response.users);
                } else {
                    setPrivateUsersList([]);
                }
            } else {
                setPrivateUsersList([]);
            }
        })();
    }, [debouncedSearch, getPrivateUsers, permission]);

    useEffect(() => {
        if (modelSharedUsers.length > 0 && firstRender) {
            setSelectedList(modelSharedUsers);
            setFirstRender(false);
        }
    }, [firstRender, modelSharedUsers]);

    return (
        <div>
            <div className={twMerge('flex justify-between items-center pb-2 w-full space-x-4', className)}>
                <span className={`text-theme1-title2 ${isMarked ? 'font-bold' : 'font-semibold'}`}>{text}</span>

                {!readOnly && (
                    <div className='flex space-x-4 items-start'>
                        <div className='flex items-center space-x-2'>
                            <div className='flex items-center space-x-2'>
                                <InputSwitch
                                    inputId={text}
                                    name={text}
                                    checked={isMarked}
                                    onChange={handleOnChange}
                                    disabled={disabled}
                                />
                                {errors &&
                                    errors.map((error) => {
                                        return (
                                            <div key={error} className='mt-2.5 text-theme1-error'>
                                                {error}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {isMarked && (
                <div className='mb-2'>
                    <p className={'text-theme1-text2 font-semibold'}>Share it with:</p>
                    {renderPrivate()}
                </div>
            )}
        </div>
    );
};

export default PrivateUsersListItem;
